import { useEffect, useState, type ReactNode } from 'react';
import {
  useApproveConversationMutation, useConversationQuery, useCrmTagsQuery,
  useEditConversationMutation, useRejectConversationMutation, useRetryConversationMutation, useReopenConversationMutation,
  useUpdateContactTypeMutation,
  type CaptureCommercialStage, type CaptureIntent, type ConversationEdit,
  type ConversationMessageView, type ConversationResponse,
} from './api.js';
import { EMPTY_VOCABULARY } from './CrmTagPicker.js';
import { fromSelection, labelFor, reachesCrm, toSelection, withdrawnNote } from './crmTagSelection.js';
import { fromCapturePhones, phonesChanged, serializePhones } from './phoneList.js';
import {
  CaptureStatusBadge, INTENT_LABELS, PROVIDER_TEXT, PersonRow, WRAP, crmStatusOf, errorOutcome,
  exactWhen, h4, inputCls, personName, reasonText, sharedEdit, sharedOutcome,
  type Outcome, type PersonDraft, type SharedAction,
} from './CaptureReview.js';
import { contactTypeBlocks, contactTypeView, labelOf } from './contactType.js';
import { DANGER, DrawerActions, DrawerFooter, DrawerSource, useDrawerDirty, useDrawerDone } from './ReviewDrawer.js';
import { Alert, Button, Spinner } from './ui.js';

/**
 * A marked conversation, as Review shows it: CONTACTS first, each person ONCE, every marked
 * message kept behind a small envelope beside each person.
 *
 * Gmail applies a label to a whole conversation, so marking one thread marks each of its
 * messages, and each is its own capture — its own activity in the CRM. Here the reviewer
 * decides about people: select them, check each one's Contact Type (pre-filled from the
 * workspace default where one is set), add optional CRM Tags per person, and approve. The
 * messages are one click away, not in the way. The mailbox owner is never listed; people
 * with nothing left to send sit in a collapsed "Already sent". Selecting, typing and
 * tagging send nothing — only "Approve selected" does, through the same approval as ever.
 */

const INTENTS = Object.keys(INTENT_LABELS) as CaptureIntent[];

interface Draft {
  people: Record<string, PersonDraft>;
  messages: Record<string, { intent: CaptureIntent; summary: string }>;
  commercialStage: CaptureCommercialStage | null;
  relationshipTypeKeys: string[];
  crmTagKeys: string[];
}

const isOpen = (m: ConversationMessageView) => m.status === 'review_required';

/** Why one person was not sent, said of that person. */
const STOPPED_TEXT: Record<string, string> = {
  role_mailbox: 'this email may be shared — confirm the person before sending',
  shared_address_in_use: 'more than one Keap contact uses this email — choose the correct person',
  identity_ambiguous: 'more than one Keap contact has this address, so ContactPilot won’t pick one',
  identity_unresolved: 'Keap could not be checked for this person',
  contact_type_required: 'needs a Contact Type before Keap can create them',
};

/** The message's tags — its labels' mapping, or the last word on them — as one person's starting point. */
const leadOf = (c: ConversationResponse) => {
  const open = c.messages.filter(isOpen);
  return open[open.length - 1] ?? c.messages[c.messages.length - 1];
};
const personTags = (c: ConversationResponse, p: ConversationResponse['people'][number]): string[] => {
  const lead = leadOf(c);
  return toSelection(p.tags ?? {
    commercialStage: lead?.commercialStage ?? null,
    relationshipTypeKeys: lead?.relationshipTypeKeys ?? [], crmTagKeys: lead?.crmTagKeys ?? [],
  }, EMPTY_VOCABULARY);
};

/**
 * A new person's starting Contact Type: what is saved on them (a person's choice, kept as
 * it is), else the workspace default, else nothing. Never for someone already in Keap.
 */
const startingType = (c: ConversationResponse, p: ConversationResponse['people'][number]) =>
  p.contactType ? { contactType: p.contactType, contactTypeSource: p.contactTypeSource === 'workspace_default' ? 'workspace_default' as const : 'user' as const }
    : p.crm.kind === 'new' && c.contactTypes.workspaceDefault ? { contactType: c.contactTypes.workspaceDefault, contactTypeSource: 'workspace_default' as const }
      : { contactType: null, contactTypeSource: null };

function draftFrom(c: ConversationResponse): Draft {
  const lead = leadOf(c);
  return {
    people: Object.fromEntries(c.people.map((p) => [p.id, {
      include: p.include, ...startingType(c, p), displayName: p.displayName ?? '',
      phones: fromCapturePhones(p.phones), title: p.title?.value ?? '',
      company: p.company?.value ?? '', website: p.website?.value ?? '',
      tags: personTags(c, p),
    }])),
    messages: Object.fromEntries(c.messages.map((m) => [m.captureId, { intent: m.intent ?? 'unknown', summary: m.summary ?? '' }])),
    commercialStage: lead?.commercialStage ?? null,
    relationshipTypeKeys: [...(lead?.relationshipTypeKeys ?? [])],
    crmTagKeys: [...(lead?.crmTagKeys ?? [])],
  };
}

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** Only what changed, and only for people on an open message. */
function editFrom(c: ConversationResponse, d: Draft): { body: ConversationEdit; dirty: boolean } {
  const base = draftFrom(c);
  const open = c.messages.filter(isOpen);
  const people: ConversationEdit['people'] = [];
  let unsaved = false;
  for (const p of c.people) {
    if (p.openCaptureIds.length === 0) continue;
    const now = d.people[p.id]; const was = base.people[p.id];
    if (!now || !was) continue;
    const e: ConversationEdit['people'][number] = { id: p.id, include: now.include };
    let changed = now.include !== was.include;
    for (const k of ['displayName', 'title', 'company', 'website'] as const) {
      if (now[k].trim() !== was[k].trim()) { e[k] = now[k].trim() || null; changed = true; }
    }
    if (phonesChanged(now.phones, was.phones)) { e.phones = serializePhones(now.phones); changed = true; }
    /*
     * A type differing from what is SAVED is sent — a pre-filled default included, so
     * approving keeps it. Only a change from what was on screen makes the drawer "unsaved".
     */
    if (now.contactType !== (p.contactType ?? null)) {
      e.contactType = now.contactType;
      if (now.contactType) e.contactTypeSource = now.contactTypeSource === 'workspace_default' ? 'workspace_default' : 'user';
      unsaved ||= now.contactType !== was.contactType;
      changed = true;
    }
    if (!sameSet(now.tags ?? [], was.tags ?? [])) {
      e.tags = fromSelection(now.tags ?? [], EMPTY_VOCABULARY) as NonNullable<ConversationEdit['people'][number]['tags']>;
      changed = true; unsaved = true;
    }
    if (now.include !== was.include || ['displayName', 'title', 'company', 'website'].some((k) => e[k as 'title'] !== undefined) || e.phones) unsaved = true;
    if (changed) people.push(e);
  }
  const messages: ConversationEdit['messages'] = [];
  for (const m of open) {
    const now = d.messages[m.captureId]; const was = base.messages[m.captureId];
    if (!now || !was) continue;
    const x: ConversationEdit['messages'][number] = { captureId: m.captureId };
    if (now.intent !== was.intent) x.intent = now.intent;
    if (now.summary.trim() !== was.summary.trim()) x.summary = now.summary.trim() || null;
    if (x.intent !== undefined || x.summary !== undefined) messages.push(x);
  }
  // Tags are the conversation's: a change, or any open message that differs, is sent.
  const tagsDiffer = open.some((m) => m.commercialStage !== d.commercialStage
    || !sameSet(m.relationshipTypeKeys, d.relationshipTypeKeys) || !sameSet(m.crmTagKeys, d.crmTagKeys));
  const tagsChanged = d.commercialStage !== base.commercialStage
    || !sameSet(d.relationshipTypeKeys, base.relationshipTypeKeys) || !sameSet(d.crmTagKeys, base.crmTagKeys);
  return {
    body: {
      versions: Object.fromEntries(open.map((m) => [m.captureId, m.updatedAt])),
      people, messages,
      commercialStage: d.commercialStage, relationshipTypeKeys: d.relationshipTypeKeys, crmTagKeys: d.crmTagKeys,
    },
    dirty: unsaved || messages.length > 0 || tagsChanged || tagsDiffer,
  };
}

export function ConversationDetail({ id }: { id: string }) {
  const { data, isLoading, isFetching, error, refetch } = useConversationQuery(id);
  const tags = useCrmTagsQuery();
  const [edit, { isLoading: saving }] = useEditConversationMutation();
  const [approve, { isLoading: approving }] = useApproveConversationMutation();
  const [reject, { isLoading: rejecting }] = useRejectConversationMutation();
  const [retry, { isLoading: retrying }] = useRetryConversationMutation();
  const [reopen, { isLoading: reopening }] = useReopenConversationMutation();
  const [updateType, { isLoading: updatingType }] = useUpdateContactTypeMutation();
  /*
   * Proposed Contact Type changes for people already in Keap, by person: the Keap contact,
   * the value the person saw, the value they chose, and one id per proposal (so a retry or a
   * second click is the same change). Nothing is sent until Approve selected.
   */
  const [proposals, setProposals] = useState<Record<string, { ext: string; from: string | null; to: string; requestId: string }>>({});
  const [typeNotice, setTypeNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const done = useDrawerDone();
  const vocab = tags.data ?? EMPTY_VOCABULARY;

  const version = data ? data.messages.map((m) => `${m.captureId}@${m.updatedAt}`).join() : null;
  useEffect(() => { if (data) setDraft(draftFrom(data)); }, [version]);
  useEffect(() => { setOutcome(null); setProposals({}); setTypeNotice(null); }, [id]);
  /*
   * A proposal is of one contact and one value as seen. If Keap's value or the matched
   * contact moved since, the proposal is dropped and the person is told, never carried over.
   */
  useEffect(() => {
    if (!data) return;
    const stale = Object.entries(proposals).filter(([pid, x]) => {
      const p = data.people.find((q) => q.id === pid);
      const live = data.keapContactTypes?.[x.ext];
      return !p || p.crm.kind !== 'existing' || p.crm.externalContactId !== x.ext || !live || live.status !== 'ok' || live.contactType !== x.from;
    });
    if (stale.length === 0) return;
    setProposals((all) => Object.fromEntries(Object.entries(all).filter(([pid]) => !stale.some(([s]) => s === pid))));
    setTypeNotice('This contact changed in Keap. Review the latest information before continuing.');
  }, [data]);

  const open = data ? data.messages.filter(isOpen) : [];
  const editable = open.length > 0;
  const busy = saving || approving || rejecting || retrying || reopening || updatingType;
  // Approved messages that have not all reached the CRM yet: a daily limit, a switch, a failure.
  const unsent = data ? data.messages.filter((m) => ['partially_executed', 'failed', 'held'].includes(m.status)) : [];
  /*
   * Stopped by something a person must change (a shared mailbox, an ambiguous match): Retry
   * alone can never finish it, so the action says what it does — takes it back to Review,
   * sending nothing, where the person can change it.
   */
  const forPerson = unsent.some((m) => m.stoppedBy === 'person');
  // Only these are retried: a send a gate held or the CRM did not complete. Never a person's correction.
  const retryable = unsent.filter((m) => m.stoppedBy !== 'person');
  const pending = data && draft ? editFrom(data, draft) : null;
  useDrawerDirty(Boolean(editable && pending?.dirty));
  const stale = data?.messages.some((m) => isOpen(m) && m.stale) ?? false;

  const included = data && draft
    ? data.people.filter((p) => draft.people[p.id]?.include && !p.isInternal && p.email && p.openCaptureIds.length > 0)
    : [];
  // An included person with no safe CRM destination; the reason is shown beside them.
  const undeliverable = included.find((p) => p.delivery?.state !== 'sent'
    && ((p.isRoleMailbox && !p.sharedAddressConfirmation) || p.crm.kind === 'ambiguous' || p.crm.kind === 'unresolved'));
  const blocked = editable && draft && data
    ? ((undeliverable ? `${personName(undeliverable)} needs a decision before sending — see the note beside them.` : null)
      ?? withdrawnNote([...new Set(included.flatMap((p) => draft.people[p.id]?.tags ?? []))], vocab)
      ?? contactTypeBlocks(included.map((p) => contactTypeView({
        types: data.contactTypes, status: crmStatusOf(p), value: draft.people[p.id]?.contactType,
        existingValue: p.existingContactType ?? null,
      })))
      ?? (included.length === 0 && Object.keys(proposals).length === 0 ? 'Select at least one person to include before approving.' : null))
    : null;

  const settle = (o: Outcome) => {
    if (done && o.tone !== 'error' && !o.retry && !o.reload) done(o.text);
    else setOutcome(o);
  };

  const save = async (): Promise<Record<string, string> | null> => {
    if (!data || !pending) return null;
    try {
      const r = await edit({ id, body: pending.body }).unwrap();
      return Object.fromEntries(r.saved.map((s) => [s.captureId, s.planHash]));
    } catch (e) {
      setOutcome(errorOutcome(e));
      return null;
    }
  };

  /** Each proposed Contact Type change, one at a time; every result said, none hidden in another. */
  const runTypeUpdates = async (): Promise<{ done: string[]; problems: string[] }> => {
    const done: string[] = []; const problems: string[] = [];
    if (!data) return { done, problems };
    const label = (v: string | null) => (v ? labelOf(data.contactTypes, v) : 'Not set');
    for (const [pid, x] of Object.entries(proposals)) {
      const p = data.people.find((q) => q.id === pid);
      const name = p ? personName(p) : 'A contact';
      try {
        const r = await updateType({ externalContactId: x.ext, expected: x.from, contactType: x.to, requestId: x.requestId,
          source: { kind: 'email', captureId: id, personId: pid } }).unwrap();
        if (r.status === 'checking') problems.push(`${name}: Keap did not confirm the change yet. ContactPilot will check before doing anything else.`);
        else done.push(`${name} (${label(x.from)} → ${label(x.to)})`);
        setProposals((all) => { const n = { ...all }; delete n[pid]; return n; });
      } catch (e) {
        const body = (e as { data?: { error?: string; current?: string | null; message?: string } })?.data;
        problems.push(body?.error === 'contact_type_changed'
          ? `${name}: this contact changed in Keap. Previously reviewed ${label(x.from)}, now ${label(body.current ?? null)} in Keap, your change ${label(x.to)}. Review the latest Contact Type before updating.`
          : `${name}: ${body?.message ?? 'We couldn’t update the Contact Type. Review the issue before trying again.'}`);
        if (body?.error === 'contact_type_changed') setProposals((all) => { const n = { ...all }; delete n[pid]; return n; });
      }
    }
    return { done, problems };
  };

  const onApprove = async () => {
    if (!data || !pending) return;
    setOutcome(null); setTypeNotice(null);
    // Only Contact Type changes to make (nobody selected, or nothing left to send): just those.
    if (!editable || included.length === 0) {
      const t = await runTypeUpdates();
      await refetch();
      setOutcome(t.problems.length > 0
        ? { tone: 'error', text: [...(t.done.length ? [`Contact Type updated in Keap: ${t.done.join(', ')}.`] : []), ...t.problems].join(' ') }
        : { tone: 'success', text: `Contact Type updated in Keap: ${t.done.join(', ')}.` });
      return;
    }
    /*
     * Always saved first: the save is what binds every open message to what is on screen,
     * including the CRM's newest answer about each person, and returns the hashes to approve.
     */
    const saved = await save();
    if (!saved) return;
    const hashes = { ...Object.fromEntries(open.map((m) => [m.captureId, m.planHash])), ...saved };
    try {
      const r = await approve({ id, planHashes: hashes }).unwrap();
      const sent = r.results.filter((x) => x.status === 'executed').length;
      const refused = r.results.find((x) => x.refusal && x.status !== 'rejected');
      // The Contact Type changes the person approved, each its own operation, after the send.
      const t = Object.keys(proposals).length > 0 && r.crmWriteEnabled ? await runTypeUpdates() : { done: [], problems: [] };
      const typed = t.done.length > 0 ? ` Contact Type updated in Keap: ${t.done.join(', ')}.` : '';
      if (t.problems.length > 0) { await refetch(); setOutcome({ tone: 'error', text: `${r.executed ? `Sent to your CRM: ${sent} message${sent === 1 ? '' : 's'} of activity.` : 'Not everything was sent.'}${typed} ${t.problems.join(' ')}`, reload: true }); return; }
      if (!r.crmWriteEnabled) settle({ tone: 'info', text: 'Approved. Nothing was sent to your CRM because CRM sending is switched off.' });
      else if (r.executed) settle({ tone: 'success', text: `Sent to your CRM: ${sent} message${sent === 1 ? '' : 's'} of activity.${typed}` });
      else if (refused?.refusal === 'contact_type_required') setOutcome({ tone: 'error', text: 'Nothing more was sent. Choose a Contact Type your CRM offers for each new contact, then approve again.', reload: true });
      else if (refused?.refusal === 'needs_person') setOutcome({ tone: 'error', text: 'Not everyone could be sent. It is back in Review — the reason is shown beside each person who needs a change.', reload: true });
      else setOutcome({ tone: 'error', text: 'Approved, but not everything reached your CRM yet. Open the conversation again to retry.', reload: true });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  /** "Review and fix": the stopped messages back to Review. Nothing is sent or retried. */
  const onReopen = async () => {
    setOutcome(null);
    try {
      await reopen({ id }).unwrap();
      await refetch();
      setOutcome({ tone: 'info', text: 'Back in Review. Nothing was sent, and what already reached Keap is kept. Make the change shown beside the person, then approve.' });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onRetry = async () => {
    setOutcome(null);
    try {
      const r = await retry({ id }).unwrap();
      if (r.executed) settle({ tone: 'success', text: 'Sent to your CRM.' });
      else if (r.results.some((x) => x.refusal === 'needs_person')) {
        // The CRM changed under a retried send; it is editable again. Shown at once.
        await refetch();
        setOutcome({ tone: 'info', text: 'Back in Review. Something needs a change before it can be sent — see the note beside the person.' });
      }
      else if (r.results.some((x) => x.refusal === 'contact_type_required')) setOutcome({ tone: 'error', text: 'A new contact still needs a Contact Type your CRM offers.', reload: true });
      else setOutcome({ tone: 'info', text: 'Not everything could be sent yet — for example, today\'s CRM limit was reached. Nothing was lost; try again later.', reload: true });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onReject = async () => {
    setOutcome(null);
    try {
      await reject({ id }).unwrap();
      settle({ tone: 'info', text: 'Rejected. Nothing was sent to your CRM, and the emails are unchanged.' });
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  const onSaveOnly = async () => {
    setOutcome(null);
    if (await save()) setOutcome({ tone: 'success', text: 'Changes saved. Nothing has been sent to your CRM yet.' });
  };

  /**
   * A shared-address decision for one person, saved at once with whatever else is on
   * screen, so Keap's answer about them shows before a Contact Type or an approval.
   */
  const onShared = async (pid: string, action: SharedAction) => {
    if (!data || !pending || !draft) return;
    setOutcome(null);
    const body = pending.body;
    const mine = sharedEdit(body.people.find((x) => x.id === pid), pid, draft.people[pid]!, action);
    try {
      await edit({ id, body: { ...body, people: [...body.people.filter((x) => x.id !== pid), mine] } }).unwrap();
      await refetch();
      setOutcome(sharedOutcome(action));
    } catch (e) {
      setOutcome(errorOutcome(e));
    }
  };

  if (isLoading) return <Spinner label="Loading this conversation…" />;
  if (error && !data) return <Alert tone="error">This conversation could not be loaded.</Alert>;
  if (!data || !draft) return null;

  const patchPerson = (pid: string, p: Partial<PersonDraft>) =>
    setDraft((d) => (d ? { ...d, people: { ...d.people, [pid]: { ...d.people[pid]!, ...p } } } : d));
  const patchMessage = (cid: string, p: Partial<Draft['messages'][string]>) =>
    setDraft((d) => (d ? { ...d, messages: { ...d.messages, [cid]: { ...d.messages[cid]!, ...p } } } : d));
  /*
   * Who is listed. The mailbox owner (and every connected mailbox and member) never is: they
   * stay on the messages, for who-wrote-to-whom, but they are not a contact to send. People
   * with nothing left to send — every message they are on decided — sit in "Already sent".
   */
  const external = data.people.filter((p) => !p.isInternal && p.email);
  const active = editable ? external.filter((p) => p.openCaptureIds.length > 0) : external.filter((p) => p.delivery?.state !== 'sent');
  const finished = external.filter((p) => !active.includes(p));
  /** Who "Select all" may tick: on an open message, with one safe CRM destination. Never a shared email nobody confirmed. */
  const eligible = (p: (typeof external)[number]) => p.openCaptureIds.length > 0
    && (!p.isRoleMailbox || Boolean(p.sharedAddressConfirmation))
    && p.crm.kind !== 'ambiguous' && p.crm.kind !== 'unresolved';
  const selectable = active.filter(eligible);
  const selected = selectable.filter((p) => draft.people[p.id]?.include);
  const allSelected = selectable.length > 0 && selected.length === selectable.length;
  const setAll = (on: boolean) => setDraft((d) => (d ? {
    ...d, people: { ...d.people, ...Object.fromEntries(selectable.map((p) => [p.id, { ...d.people[p.id]!, include: on }])) },
  } : d));
  /** One Contact Type for every selected NEW person; someone already in Keap keeps theirs. */
  const setTypeForSelected = (contactType: string) => setDraft((d) => (d ? {
    ...d, people: { ...d.people, ...Object.fromEntries(selected.filter((p) => p.crm.kind === 'new')
      .map((p) => [p.id, { ...d.people[p.id]!, contactType, contactTypeSource: 'user' as const }])) },
  } : d));
  /** One tag added to every selected person's own tags; nobody else's change. */
  const addTagToSelected = (tagId: string) => setDraft((d) => (d ? {
    ...d, people: { ...d.people, ...Object.fromEntries(selected
      .map((p) => [p.id, { ...d.people[p.id]!, tags: [...new Set([...(d.people[p.id]!.tags ?? []), tagId])] }])) },
  } : d));
  const selectedNew = selected.filter((p) => p.crm.kind === 'new').length;
  const tagChoices = vocab.tags.filter((t) => reachesCrm(t.id, vocab));
  const markers = [...new Set(data.messages.flatMap((m) => m.markers))];
  const markerWord = data.provider === 'gmail' ? (markers.length === 1 ? 'Label' : 'Labels') : (markers.length === 1 ? 'Category' : 'Categories');
  // A missing Contact Type is said beside Approve, from what is on screen (a pre-filled default counts), not from the saved reason.
  const warnings = [...new Set(open.flatMap((m) => m.reviewReasons).filter((r) => r !== 'review_mode' && r !== 'contact_type_required').map(reasonText))];
  const newest = data.messages[data.messages.length - 1]!;
  // Why each unsent person stopped, once each, in words: the person, then the reason.
  const unsentReasons = [...new Set(data.people.flatMap((p) => {
    if (!p.delivery || p.delivery.state === 'sent' || !p.delivery.blockedBy) return [];
    const code = p.delivery.blockedBy === 'no_contact'
      ? (p.isRoleMailbox ? 'role_mailbox' : p.crm.kind === 'ambiguous' ? 'identity_ambiguous' : p.crm.kind === 'unresolved' ? 'identity_unresolved' : 'no_contact')
      : p.delivery.blockedBy;
    return [`${personName(p)}: ${STOPPED_TEXT[code] ?? reasonText(code)}`];
  }))];
  if (unsentReasons.length === 0 && unsent.length > 0) unsentReasons.push(...new Set(unsent.flatMap((m) => m.reviewReasons).map(reasonText)));

  const consequence = included.map((p) => {
    const name = draft.people[p.id]?.displayName.trim() || personName(p);
    const own = (draft.people[p.id]?.tags ?? []).filter((t) => reachesCrm(t, vocab)).map((t) => labelFor(t, vocab));
    const type = (p.crm.kind === 'new' && draft.people[p.id]?.contactType ? ` as ${labelOf(data.contactTypes, draft.people[p.id]!.contactType!)}` : '')
      + (own.length > 0 ? ` · Tags: ${own.join(', ')}` : '');
    if (p.delivery?.state === 'sent') return `Already sent: ${name}`;
    if (p.isRoleMailbox && !p.sharedAddressConfirmation) return `Needs confirming: ${name} (shared email)`;
    if (p.isRoleMailbox && p.crm.kind === 'ambiguous') return `Needs a decision: ${name} (choose their Keap contact)`;
    if (p.crm.kind === 'existing') return `Existing: ${name}${type}`;
    if (p.crm.kind === 'new') return `Create once: ${name}${type}`;
    if (p.crm.kind === 'ambiguous') return `Can’t send: ${name} (more than one Keap contact)`;
    return `Can’t send: ${name} (Keap not checked)`;
  });

  const label = (v: string | null) => (v ? labelOf(data.contactTypes, v) : 'Not set');
  const typeChanges = Object.entries(proposals).map(([pid, x]) => {
    const p = data.people.find((q) => q.id === pid);
    return `Update existing Keap contact: ${p ? personName(p) : 'a contact'} — Contact Type ${label(x.from)} → ${label(x.to)}`;
  });
  /** What each person still has waiting, in words: the identity is one thing, the activity another. */
  const statusOf = (p: (typeof external)[number]): string | null => {
    const parts: string[] = [];
    if (p.crm.kind === 'existing' && p.openCaptureIds.length > 0) parts.push(`${p.openCaptureIds.length} email${p.openCaptureIds.length === 1 ? '' : 's'} to add to Keap`);
    if (proposals[p.id]) parts.push('Contact Type update pending');
    return parts.length > 0 ? parts.join(' · ') : null;
  };
  const typeProps = (p: (typeof external)[number]) => {
    if (p.crm.kind !== 'existing') return {};
    const ext = p.crm.externalContactId;
    const keap = data.keapContactTypes?.[ext];
    if (!keap) return {};
    return {
      keap, proposedType: proposals[p.id]?.to ?? null,
      onRetryRead: () => { void refetch(); },
      onProposeType: (to: string | null) => setProposals((all) => {
        const n = { ...all };
        if (to === null) delete n[p.id];
        else n[p.id] = { ext, from: keap.contactType, to, requestId: n[p.id]?.requestId ?? crypto.randomUUID() };
        return n;
      }),
    };
  };

  /*
   * Two actions, each named for what it does. "Review and fix" only reopens messages that
   * stopped for a person — it sends nothing. "Retry sending" resends what a gate held or
   * the CRM did not complete, and never touches a message waiting for a person.
   * Both are disabled over unsaved edits: they reload the conversation.
   */
  const recoveryButtons = (primaryFirst: boolean): ReactNode[] => [
    ...(forPerson ? [(
      <Button key="reopen" variant={primaryFirst ? 'primary' : 'secondary'} disabled={busy || Boolean(editable && pending?.dirty)} onClick={() => void onReopen()}>
        {reopening ? 'Opening…' : 'Review and fix'}
      </Button>
    )] : []),
    ...(retryable.length > 0 ? [(
      <Button key="retry" variant={primaryFirst && !forPerson ? 'primary' : 'secondary'} disabled={busy || Boolean(editable && pending?.dirty)} onClick={() => void onRetry()}>
        {retrying ? 'Retrying…' : `Retry sending (${retryable.length})`}
      </Button>
    )] : []),
  ];
  const secondary: ReactNode[] = [];
  if (editable) {
    secondary.push(
      <Button key="save" variant="secondary" disabled={busy || !pending?.dirty} onClick={() => void onSaveOnly()}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>,
      <Button key="reject" variant="secondary" className={DANGER} disabled={busy} onClick={() => void onReject()}>
        Reject / No action
      </Button>,
    );
  }
  // With nothing left to decide, recovery is the drawer's own action; a second one sits beside it.
  const typeOnly = !editable && typeChanges.length > 0;
  if (!editable) secondary.push(...recoveryButtons(true).slice(typeOnly ? 0 : 1));
  const primary = editable || typeOnly ? (
    <Button disabled={busy || Boolean(blocked) || stale} onClick={() => void onApprove()}>
      {approving || updatingType ? 'Approving…' : typeOnly ? 'Approve change' : 'Approve selected'}
    </Button>
  ) : unsent.length > 0 ? recoveryButtons(true)[0] : undefined;

  return (
    <div className="min-w-0 space-y-5" data-conversation-detail="">
      <DrawerSource>
        <p className={WRAP} data-captured-from="">
          Captured from {PROVIDER_TEXT[data.provider]}
          {markers.length > 0 ? <> · {markerWord}: {markers.join(', ')}</> : null}
          {' · '}{data.messages.length} marked messages
        </p>
      </DrawerSource>

      {warnings.length > 0 ? (
        <div className="min-w-0 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <p className="font-medium">Check before approving</p>
          <ul className="mt-1 list-inside list-disc space-y-0.5" aria-label="Why this needs a look">
            {warnings.map((t) => <li key={t} className={WRAP}>{t}</li>)}
          </ul>
        </div>
      ) : null}

      {unsent.length > 0 ? (
        <div className="min-w-0 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900" data-unsent-note="">
          <p className="font-medium">
            {forPerson ? 'Not everyone could be sent' : unsent.every((m) => m.stoppedBy === 'hold') ? 'Waiting to send' : 'Not everything reached your CRM'}
          </p>
          <ul className="mt-1 list-inside list-disc space-y-0.5">
            {unsentReasons.map((t) => <li key={t} className={WRAP}>{t}</li>)}
          </ul>
          <p className={`mt-1 ${WRAP}`}>
            {forPerson
              ? 'What already reached Keap is kept. Review and fix reopens it so you can confirm or change who is included. Nothing is sent until you approve.'
              : unsent.every((m) => m.stoppedBy === 'hold')
                ? 'Nothing failed. It sends under the same approval when you try again.'
                : 'Retrying is safe: anything that already reached Keap is not sent twice.'}
          </p>
          {/* Beside messages still to decide, the action sits with its reason, never folded away on a phone. */}
          {editable ? <div className="mt-2 grid min-w-0 gap-2 md:flex">{recoveryButtons(false)}</div> : null}
        </div>
      ) : null}

      <fieldset disabled={!editable || busy} className="min-w-0 space-y-5">
        <section className="min-w-0" aria-labelledby={`people-${id}`}>
          <h4 id={`people-${id}`} className={h4}>{editable ? 'Select contacts to send to Keap' : 'Contacts'}</h4>
          <p className="mt-1 text-xs text-slate-500" data-conversation-summary="">
            {data.messages.length} email{data.messages.length === 1 ? '' : 's'} captured · {external.length} contact{external.length === 1 ? '' : 's'}
          </p>

          {editable && selectable.length > 0 ? (
            <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2" data-bulk-actions="">
              <label className="inline-flex min-h-11 items-center gap-3 text-sm text-slate-800">
                <input
                  type="checkbox" checked={allSelected} onChange={(e) => setAll(e.target.checked)}
                  className="h-5 w-5 rounded border-slate-300" data-select-all=""
                />
                {allSelected ? 'Deselect all' : `Select all (${selectable.length})`}
              </label>
              {selectedNew > 0 && data.contactTypes.supported && data.contactTypes.available ? (
                <label className="block min-w-0">
                  <span className="sr-only">Set Contact Type for the {selectedNew} selected new contacts</span>
                  <select
                    value="" onChange={(e) => { if (e.target.value) setTypeForSelected(e.target.value); }}
                    className="min-h-11 w-full min-w-0 rounded-md border border-slate-300 bg-white px-3 text-sm" data-bulk-type=""
                  >
                    <option value="">Set Contact Type…</option>
                    {data.contactTypes.options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </label>
              ) : null}
              {selected.length > 0 && tagChoices.length > 0 ? (
                <label className="block min-w-0">
                  <span className="sr-only">Add a CRM tag to the {selected.length} selected contacts</span>
                  <select
                    value="" onChange={(e) => { if (e.target.value) addTagToSelected(e.target.value); }}
                    className="min-h-11 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-bulk-tag=""
                  >
                    <option value="">Add tag to selected…</option>
                    {tagChoices.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}

          <ul className="mt-2 space-y-2" data-active-contacts="">
            {active.map((p) => (
              <PersonRow
                key={p.id}
                p={p}
                d={draft.people[p.id]!}
                editable={editable && !busy && p.openCaptureIds.length > 0}
                onChange={(x) => patchPerson(p.id, x)}
                deliveries={data.phoneDelivery?.[p.id]}
                types={data.contactTypes}
                roles={p.roles}
                messages={p.captureIds.length}
                delivery={p.delivery ?? null}
                onShared={(a) => void onShared(p.id, a)}
                sharedBusy={busy}
                tagPicker={{ vocab, selected: draft.people[p.id]!.tags ?? [], onChange: (ids) => patchPerson(p.id, { tags: ids }) }}
                renderMessages={() => <PersonMessages person={p} messages={data.messages} />}
                statusLine={statusOf(p)}
                {...typeProps(p)}
              />
            ))}
          </ul>
          {active.length === 0 ? <p className="mt-2 text-sm text-slate-600">No one left to send in this conversation.</p> : null}

          {finished.length > 0 ? (
            <details className="mt-3 min-w-0 rounded-md border border-slate-200" data-already-sent="">
              <summary className="flex min-h-11 cursor-pointer items-center px-3 text-sm font-medium text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900">
                Already sent or decided ({finished.length})
              </summary>
              {/* The same card, nothing to select: a person in Keap can still have their Contact Type changed. */}
              <ul className="space-y-2 px-3 pb-3">
                {finished.map((p) => (
                  <PersonRow
                    key={p.id} p={p} d={draft.people[p.id]!} editable={false} hideSelect
                    onChange={() => undefined} types={data.contactTypes} roles={p.roles}
                    messages={p.captureIds.length} delivery={p.delivery ?? null}
                    renderMessages={() => <PersonMessages person={p} messages={data.messages} />}
                    statusLine={statusOf(p)}
                    {...typeProps(p)}
                  />
                ))}
              </ul>
            </details>
          ) : null}
        </section>

        {/* Every message, collapsed: each person's own are behind their envelope. */}
        <details className="min-w-0 rounded-md border border-slate-200" data-all-messages="">
          <summary className="flex min-h-11 cursor-pointer items-center px-3 text-sm font-medium text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900">
            All emails ({data.messages.length}){editable ? ' — edit intent and summary' : ''}
          </summary>
          <ol className="space-y-2 px-3 pb-3">
            {data.messages.map((m) => {
              const md = draft.messages[m.captureId]!;
              const canEdit = editable && isOpen(m);
              return (
                <li key={m.captureId} className="min-w-0 rounded-md border border-slate-200 p-3" data-conversation-message="">
                  <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={`text-sm font-medium text-slate-900 ${WRAP}`}>
                        {m.fromDisplayName ?? m.from ?? 'Unknown sender'}
                      </p>
                      <p className="text-xs text-slate-600">
                        <time dateTime={m.occurredAt}>{exactWhen(m.occurredAt)}</time>
                      </p>
                    </div>
                    {!isOpen(m) ? <CaptureStatusBadge status={m.status} /> : null}
                  </div>
                  {canEdit ? (
                    <div className="mt-2 grid min-w-0 gap-2">
                      <label className="block text-sm text-slate-700" htmlFor={`intent-${m.captureId}`}>
                        Intent
                        <select
                          id={`intent-${m.captureId}`} value={md.intent} className={inputCls}
                          onChange={(e) => patchMessage(m.captureId, { intent: e.target.value as CaptureIntent })}
                        >
                          {INTENTS.map((k) => <option key={k} value={k}>{INTENT_LABELS[k]}</option>)}
                        </select>
                      </label>
                      <label className="block text-sm text-slate-700" htmlFor={`summary-${m.captureId}`}>
                        Summary
                        <textarea
                          id={`summary-${m.captureId}`} value={md.summary} maxLength={420} rows={2}
                          onChange={(e) => patchMessage(m.captureId, { summary: e.target.value })}
                          className="mt-1 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                      </label>
                    </div>
                  ) : (
                    <p className={`mt-1 text-sm text-slate-700 ${WRAP}`}>
                      {m.intentLabel ?? INTENT_LABELS[m.intent ?? 'unknown']}{m.summary ? ` — ${m.summary}` : ''}
                    </p>
                  )}
                  {m.webLink ? (
                    <a
                      href={m.webLink} target="_blank" rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
                    >
                      View original email
                    </a>
                  ) : null}
                </li>
              );
            })}
          </ol>
          <p className={`px-3 pb-3 text-xs text-slate-500 ${WRAP}`}>Subject: {newest.subject ?? 'No subject'}</p>
        </details>
      </fieldset>

      <section className="min-w-0 rounded-md bg-slate-50 p-3">
        <h4 className={h4}>CRM consequence</h4>
        {consequence.length === 0 ? (
          typeChanges.length > 0 ? (
            <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
              {typeChanges.map((l) => <li key={l} className={`font-medium ${WRAP}`} data-type-change="">{l}</li>)}
            </ul>
          ) : <p className="mt-1 text-sm text-slate-600">No one is selected, so nothing would be sent.</p>
        ) : (
          <ul className="mt-1 space-y-0.5 text-sm text-slate-800">
            {consequence.map((l) => <li key={l} className={WRAP}>{l}</li>)}
            <li className={WRAP}>Activity: {open.length} message{open.length === 1 ? '' : 's'}, each a note on the selected people who were on it</li>
            {typeChanges.map((l) => <li key={l} className={`font-medium ${WRAP}`} data-type-change="">{l}</li>)}
          </ul>
        )}
        {!data.crmWriteEnabled ? (
          <p className="mt-2 text-xs text-slate-500">CRM sending is switched off, so approving records your decision without sending anything.</p>
        ) : null}
      </section>

      {stale ? <Alert tone="error">This conversation changed since its plan was made. Reload it before approving.</Alert> : null}
      {typeNotice ? <Alert tone="info">{typeNotice}</Alert> : null}
      <DrawerFooter>
        <DrawerActions primary={primary} secondary={secondary}>
          {blocked ? <p className="text-xs text-amber-900 [overflow-wrap:anywhere]" data-blocked-note="">{blocked}</p> : null}
          <div aria-live="polite" data-capture-actions="">
            {outcome ? (
              <Alert tone={outcome.tone}>
                {outcome.text}
                {outcome.reload || stale ? (
                  <button
                    type="button" disabled={isFetching} onClick={() => { setOutcome(null); void refetch(); }}
                    className="ml-2 inline-flex min-h-11 items-center font-medium underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-slate-900"
                  >
                    Reload
                  </button>
                ) : null}
              </Alert>
            ) : null}
          </div>
        </DrawerActions>
      </DrawerFooter>
    </div>
  );
}

/**
 * One person's emails, behind their envelope: only the messages they were on (for a shared
 * address, only those carrying their name), each with what it says and whether it reached
 * Keap for them. Read-only; opening it sends nothing.
 */
function PersonMessages({ person, messages }: { person: ConversationResponse['people'][number]; messages: readonly ConversationMessageView[] }) {
  const mine = messages.filter((m) => person.captureIds.includes(m.captureId));
  const pid = person.participantId ?? person.id;
  return (
    <ol className="min-w-0 space-y-2 rounded-md bg-slate-50 p-2" aria-label={`Emails with ${personName(person)}`}>
      {mine.map((m) => {
        const run = (m.execution as Record<string, { noteWritten?: boolean; state?: string } | undefined> | undefined)?.[pid];
        const activity = run?.noteWritten ? 'Added to Keap' : isOpen(m) ? 'Not sent yet' : run ? 'Not added' : 'Not part of a send';
        return (
          <li key={m.captureId} className="min-w-0 rounded border border-slate-200 bg-white p-2 text-sm" data-person-message="">
            <div className="flex min-w-0 flex-wrap items-baseline justify-between gap-2">
              <time className="text-xs text-slate-600" dateTime={m.occurredAt}>{exactWhen(m.occurredAt)}</time>
              <span className="text-xs font-medium text-slate-600">{activity}</span>
            </div>
            <p className={`mt-0.5 font-medium text-slate-900 ${WRAP}`}>{m.subject ?? 'No subject'}</p>
            <p className={`text-xs text-slate-600 ${WRAP}`}>
              From {m.fromDisplayName ?? m.from ?? 'unknown'}{m.to.length > 0 ? ` · To ${m.to.join(', ')}` : ''}{m.cc.length > 0 ? ` · Cc ${m.cc.join(', ')}` : ''}
            </p>
            <p className={`mt-1 text-slate-700 ${WRAP}`}>{m.intentLabel ?? INTENT_LABELS[m.intent ?? 'unknown']}{m.summary ? ` — ${m.summary}` : ''}</p>
            {m.webLink ? (
              <a href={m.webLink} target="_blank" rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center text-sm text-slate-700 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900">
                View original email
              </a>
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
